import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { gr } from "../tokens";

const TOUCH = "@media (pointer: coarse), (max-width: 899.95px)";

// Single-select filter chips. options = [{ value, label, count? }]. Each chip is a button with aria-pressed.
// variant "solid": 36px chips with control radius; the selected one is filled ink (e.g. All / Checked in / Missed).
export default function GrFilterChips({
  value,
  onChange,
  options,
  ariaLabel,
  variant = "pill",
  sx,
}) {
  const solid = variant === "solid";
  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{ display: "flex", flexWrap: "wrap", gap: gr.space[2], ...sx }}
    >
      {options.map((o) => {
        const on = o.value === value;
        const onBorder = solid ? gr.color.ink : "transparent";
        return (
          <ButtonBase
            key={o.value}
            disableRipple
            aria-pressed={on}
            onClick={() => onChange(o.value)}
            sx={{
              minHeight: solid
                ? `calc(${gr.control.sm} + ${gr.space[1]})`
                : gr.control.sm,
              px: gr.space[3],
              gap: gr.space[2],
              borderRadius: solid ? gr.radius.control : gr.radius.pill,
              border: `1px solid ${on ? onBorder : gr.color.line}`,
              background: on
                ? solid
                  ? gr.color.ink
                  : gr.color.brandSoft
                : gr.color.surface,
              color: on ? (solid ? gr.color.onBrand : gr.color.brandDark) : gr.color.ink2,
              fontFamily: gr.font.family,
              fontSize: gr.font.size.sm,
              fontWeight: on
                ? gr.font.weight.semibold
                : solid
                  ? gr.font.weight.regular
                  : gr.font.weight.medium,
              transition: "background-color .15s ease, border-color .15s ease",
              "&:hover": { borderColor: on ? onBorder : gr.color.brand },
              "&.Mui-focusVisible": {
                boxShadow: gr.shadow.focus,
                borderColor: gr.color.brand,
              },
              [TOUCH]: { minHeight: gr.control.md },
            }}
          >
            {o.label}
            {o.count !== undefined && o.count !== null ? (
              <Box
                component="span"
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  color: solid ? "inherit" : on ? gr.color.brandDark : gr.color.muted,
                }}
              >
                {o.count}
              </Box>
            ) : null}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

GrFilterChips.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
  ariaLabel: PropTypes.string,
  variant: PropTypes.oneOf(["pill", "solid"]),
  sx: PropTypes.object,
};
